// Global Instructions Rule Applied!
//
// LPs — HTTP service (FUND MANAGEMENT → LPs). Talks to the Python backend
// only (CLAUDE.md — never Supabase directly); every URL comes from
// API_ROUTES; every response is validated with its Zod model before it
// reaches UI state.
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'
import { LpRow, LpsFilterOptions, validateLpRows, validateLpsFilterOptions } from '../../../state_manager/data_models/lps_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

const jsonHeaders = (accessToken: string | null): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

// The backend's own sentence, never a bare status line (design patterns:
// "Feedback on a blocked action").
const readError = async (response: Response, fallback: string): Promise<string> => {
  const body = await readJsonBody<{ error?: string; message?: string }>(response)
  return body?.message || body?.error || `${fallback}: ${response.status} ${response.statusText}`
}

const request = async (url: string, init: RequestInit, accessToken: string | null, fallback: string): Promise<any> => {
  const response = await fetch(url, { ...init, headers: jsonHeaders(accessToken) })
  if (!response.ok) {
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(await readError(response, fallback))
  }
  return response.json()
}

export interface ListResult<T> {
  data: T[]
  has_more: boolean
  count: number
}

export interface LpsQuery {
  filters?: Array<{ key: string; value: unknown }>
  sorting?: Array<{ key: string; direction: 'asc' | 'desc' }>
  page?: number
  page_size?: number
}

export const fetchLps = async (query: LpsQuery, accessToken: string | null): Promise<ListResult<LpRow>> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.LPS.LIST)
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: query }) }, accessToken, 'Failed to load LPs')
  return { data: validateLpRows(json.data ?? []), has_more: Boolean(json.has_more), count: Number(json.count ?? 0) }
}

export const fetchLpsFilterOptions = async (accessToken: string | null): Promise<LpsFilterOptions> => {
  const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.LPS.FILTER_OPTIONS)
  const json = await request(url, { method: 'POST', body: JSON.stringify({ data: {} }) }, accessToken, 'Failed to load filter options')
  return validateLpsFilterOptions(json.data ?? {})
}
